// Names the endpoint behind an Axios failure that never got a response, as tags and a per-endpoint fingerprint, so
// "Network Error" splits by request and a failing endpoint stands out from scattered client connectivity.
import type { ErrorEvent } from '@sentry/browser'
import { isAxiosError } from 'axios'
import { TransportFailure, transportFailureOf } from './transport-failure'

const FINGERPRINT_KEY = 'axios-no-response'

const MIN_DIGIT_BEARING_ID_LENGTH = 8

const isIdSegment = (segment: string): boolean =>
  /^\d+$/.test(segment) || (segment.length >= MIN_DIGIT_BEARING_ID_LENGTH && /\d/.test(segment))

const idCollapsedEndpointOf = (url: string | undefined, baseURL: string | undefined): string => {
  try {
    const parsed = new URL(url ?? '', baseURL ?? window.location.origin)
    const template = parsed.pathname
      .split('/')
      .map((segment) => (isIdSegment(segment) ? ':id' : segment))
      .join('/')
    return `${parsed.host}${template}`
  } catch {
    return 'unparseable-url'
  }
}

/** The event with the failed request's method and endpoint when `originalException` is an Axios no-response. */
export const withNoResponseRequest = (event: ErrorEvent, originalException: unknown, online: boolean): ErrorEvent => {
  const failure = transportFailureOf(originalException, online)
  if (
    !isAxiosError(originalException) ||
    (failure !== TransportFailure.NoResponse && failure !== TransportFailure.Offline)
  ) {
    return event
  }
  const method = (originalException.config?.method ?? 'get').toUpperCase()
  const endpoint = idCollapsedEndpointOf(originalException.config?.url, originalException.config?.baseURL)
  return {
    ...event,
    tags: { ...event.tags, 'http.method': method, 'http.path': endpoint, transport_failure: failure },
    fingerprint: [FINGERPRINT_KEY, method, endpoint]
  }
}
